import TanStackTableBasic from '@src/app-components/table/TanStackTable/TanStackTableBasic';
import AddContactModal from './AddContactModal';
import ContactActionsTableCell from './ContactActionsTableCell';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Label } from '@trussworks/react-uswds';
import classnames from 'classnames';
import { connect } from 'redux-bundler-react';
import Tooltip from '@components/tooltip/tooltip';
import Icon from '@components/icon/Icon';
import { mdiPlusBox, mdiCloseOctagon } from '@mdi/js';
import { useEffect, useMemo, useState } from 'react';
import { useModal } from '@stores';
import { useFormContext } from 'react-hook-form';
import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';
import FileDownloadList from '../file-download-list/FileDownloadList';
import { ProfileRoles, ContactTypes } from '@src/utils/enums';
import { addUserProfileAgent } from '@forms/components/modals/_shared/UserProfileAgent';
import { useUserProfile } from '@hooks';

const columnHelper = createColumnHelper();

const onSave = (data, fnc) => {
  fnc((rowData) => [...rowData, data]);
};

const clearRowData = (fnc, resetProfileUsed) => {
  fnc(() => []);
  resetProfileUsed?.(false);
};

const ContactInput = connect(
  ({
    showOptionalText = true,
    isReadOnly,
    fileDownload,
    hint,
    id,
    label,
    name,
    onChange,
    required,
    tooltip,
    tooltipClickable,
    contactType = 'Contact',
    dataKey = 'Contact',
    questionId,
    questionType,
    formId,
    initialContacts = [],
    onContactRowDataChange,
    agentProfile,
    contacts,
  }) => {
    const { register, setValue } = useFormContext();
    const { openModal } = useModal();
    const { userProfileData } = useUserProfile();

    const [localRowData, setLocalRowData] = useState(initialContacts);
    const contactRowData = contacts ?? localRowData ?? initialContacts;
    const [isUserProfileAgentUsed, setIsUserProfileAgentUsed] = useState(agentProfile);
    const setContactRowData = onContactRowDataChange ?? setLocalRowData;

    const labelStyles = classnames({
      required: required,
      optional: !required && showOptionalText,
    });

    const handleAddClick = () =>
      openModal(AddContactModal, {
        addRowData: onSave,
        contactType: contactType,
        onUpdate: setContactRowData,
        rowData: contactRowData,
        isReadOnly: isReadOnly,
        userProfileData: userProfileData,
        questionType: questionType,
      });

    const contactColumnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <ContactActionsTableCell
              row={row}
              rowData={contactRowData}
              onUpdate={setContactRowData}
              modalComponent={AddContactModal}
              isReadOnly={isReadOnly}
              questionType={questionType}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
            optional: true, // These values are set to prevent error styling from rendering when inappropriate.
          },
        }),
        columnHelper.accessor('contactType', {
          header: 'Contact Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('firstName', {
          header: 'First Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('middleName', {
          header: 'Middle Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Last Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('phoneOneType', {
          header: 'Phone One Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('phoneOne', {
          header: 'Phone One',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('state', {
          header: 'State',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('country', {
          header: 'Country',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
      ],
      [contactRowData, isReadOnly, questionType, setContactRowData]
    );

    const formatContactData = (contactRowData, questionId, dataKey) =>
      contactRowData.map((contact) => {
        const formattedItem = {
          questionID: questionId,
          ...contact,
        };

        if (dataKey === 'jpaContactAnswers') {
          return {
            ...formattedItem,
            requestAnswerSetID: contact.requestAnswerSetID ?? 0,
            jpaContactAnswerID: contact.jpaContactAnswerID ?? 0,
          };
        }
        return formattedItem;
      });

    useEffect(() => {
      register(name);
    }, [register, name]);

    useEffect(() => {
      if (dataKey === 'jpaContactAnswers') {
        setValue(name, contactRowData?.length > 0 ? contactRowData : null, { shouldValidate: true });

        if (typeof onChange === 'function' && contactRowData) {
          const transformed = formatContactData(contactRowData, questionId, dataKey);
          onChange(transformed, questionId, QUESTION_TYPE.contact, formId);
        }
      } else if (dataKey === 'Contact' && typeof onContactRowDataChange === 'function') {
        onContactRowDataChange(contactRowData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactRowData, name, setValue, questionId]);

    return (
      <div id={id} className='contact-input-wrapper'>
        <Label className={labelStyles} htmlFor={name}>
          <span id={`${name}_label`}>{label}</span>
          {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
        </Label>
        {hint && (
          <div className='usa-hint' id={`${name}_hint`}>
            {hint}
          </div>
        )}
        {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
        <div className='table-button-container'>
          {contactRowData.length > 0 && (
            <Button
              className='add-contact-button'
              title='Add New Row'
              size='small'
              onClick={() =>
                openModal(AddContactModal, {
                  addRowData: onSave,
                  contactType: contactType,
                  onUpdate: setContactRowData,
                  rowData: contactRowData,
                  isReadOnly: isReadOnly,
                  userProfileData: userProfileData,
                  questionType: questionType,
                })
              }
              disabled={isReadOnly}
            >
              <Icon focusable={false} path={mdiPlusBox} size='16px' />
              Add New Row
            </Button>
          )}
          {contactType === ContactTypes.Agent && userProfileData?.role === ProfileRoles.Agent && (
            <Button
              className={`table-btn-profile ${agentProfile || isReadOnly ? 'disabled' : 'hover'}`}
              title='Use Profile Data'
              size='small'
              onClick={() =>
                addUserProfileAgent(
                  userProfileData,
                  contactRowData,
                  setContactRowData,
                  isUserProfileAgentUsed,
                  setIsUserProfileAgentUsed
                )
              }
              disabled={isReadOnly || agentProfile}
            >
              Use Profile Data
            </Button>
          )}
          {contactRowData.length !== 0 && (
            <Button
              className='clear-table-button'
              title='Remove Rows'
              size='small'
              onClick={() => clearRowData(setContactRowData, setIsUserProfileAgentUsed)}
              disabled={isReadOnly || contactRowData?.length === 0}
            >
              <Icon focusable={false} path={mdiCloseOctagon} size='16px' />
              Remove Rows
            </Button>
          )}
        </div>
        <TanStackTableBasic
          data={contactRowData}
          columns={contactColumnDefs}
          placeholderText={`No ${contactType} found.`}
          onAddClick={handleAddClick}
          isReadOnly={isReadOnly}
        />
      </div>
    );
  }
);

export default ContactInput;
